import { useCallback, useState, type CSSProperties } from 'react'
import { unsplashSrcSet } from '@/lib/coverImages'
import { clearImageFailed, imageHasFailed, markImageFailed } from './imageFallback'

interface LazyImageProps {
  src: string
  alt: string
  className?: string
  fallback?: string
  /**
   * `srcSet` to expose to the browser for responsive selection. If omitted,
   * one is auto-derived for Unsplash CDN URLs.
   */
  srcSet?: string
  /**
   * Format-negotiated alternatives, rendered as `<source>` elements ahead of
   * the `<img>`. Order them best-first (AVIF, then WebP): the browser takes the
   * first `type` it understands and never looks at the rest, falling through to
   * the `<img>`'s own `src`/`srcSet` if it understands none.
   *
   * Only self-hosted assets need this. The Unsplash CDN negotiates format on
   * its own via `auto=format`, so a hotlinked frame passes nothing here.
   */
  sources?: readonly { type: string; srcSet: string }[]
  /** `sizes` attribute paired with `srcSet`. Defaults to a sensible card-grid value. */
  sizes?: string
  /**
   * Resource hint for the browser. Set to `'high'` on the page's LCP image to
   * skip lazy loading + jump the request queue. Defaults to `auto`.
   */
  fetchPriority?: 'high' | 'low' | 'auto'
  /**
   * Whether to lazy-load. Defaults to true — set false (with fetchPriority
   * 'high') on above-the-fold LCP images.
   */
  lazy?: boolean
  /**
   * Intrinsic image dimensions in pixels. Setting both `width` and `height`
   * lets the browser reserve the right box during layout and prevents CLS.
   * Pass real pixel values from the source (or a representative size for the
   * srcSet) — CSS still controls display size via `w-full h-full`.
   */
  width?: number
  height?: number
  /**
   * Alternative to width/height when you only know the ratio (e.g., "16/9",
   * "3/2", 1.5). Applied as `aspect-ratio` on the wrapper. Ignored when
   * width+height are both provided. If neither is set AND the caller has not
   * applied an aspect-* utility class to className, we default to 16/9 so the
   * wrapper still reserves a box.
   */
  aspectRatio?: string | number
  /**
   * Classes for the box shown while the image is still loading (and left in
   * place permanently if it errors with no `fallback`). Defaults to the app's
   * neutral skeleton. Marketing surfaces pass a warm gradient instead, so a
   * slow or dead CDN never shows a flat grey rectangle.
   */
  placeholderClassName?: string
}

const DEFAULT_SIZES = '(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw'

// Heuristic: if the parent passed in an aspect-* class (Tailwind aspect-video,
// aspect-square, aspect-[3/2], etc.) or an explicit height-* class, they're
// already reserving space — don't apply our default aspectRatio on top.
function hasOwnAspect(className: string): boolean {
  return /(?:^|\s)(aspect-|h-)/.test(className)
}

export default function LazyImage({
  src,
  alt,
  className = '',
  fallback,
  srcSet,
  sources,
  sizes,
  fetchPriority = 'auto',
  lazy = true,
  width,
  height,
  aspectRatio,
  placeholderClassName = 'bg-dust animate-pulse',
}: LazyImageProps) {
  const [loaded, setLoaded] = useState(false)
  const [error, setError] = useState(false)

  const imgSrc = error && fallback ? fallback : src
  const resolvedSrcSet = srcSet ?? unsplashSrcSet(imgSrc) ?? undefined
  const resolvedSizes = resolvedSrcSet ? (sizes ?? DEFAULT_SIZES) : undefined

  // The prerendered marketing pages are the reason this exists. Their hero
  // image is frequently already decoded by the time React hydrates, so the
  // `onLoad` we attach can never fire — and an image gated purely on that
  // handler stays at `opacity-0` forever. Read the element's own completion
  // state at attach time instead of waiting to be told.
  //
  // A ref CALLBACK rather than an effect: it runs on the commit that attaches
  // the node, and re-runs when `imgSrc` changes (the error → `fallback` swap),
  // which a mount-only effect would miss.
  //
  // The same read covers the failure side: an image that already broke has no
  // `error` event left to fire either, so stamp the shared `data-img-failed`
  // marker (see `imageFallback.ts`) from here as well. That is also what
  // re-applies a mark the pre-hydration listener set and React's hydration pass
  // discarded.
  const measureRef = useCallback((node: HTMLImageElement | null) => {
    if (!node) return
    if (node.complete && node.naturalWidth > 0) setLoaded(true)
    else if (imageHasFailed(node)) markImageFailed(node)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on the resolved src so React re-attaches (and re-measures) when it swaps to the fallback
  }, [imgSrc])

  // The `<source>` list describes `src`, so it has to go the moment we swap to
  // `fallback` — otherwise the browser keeps picking a format variant of the
  // image that just failed and the fallback never renders.
  const resolvedSources = error && fallback ? undefined : sources

  const hasDims = typeof width === 'number' && typeof height === 'number'
  const wrapperStyle: CSSProperties | undefined = (() => {
    if (hasDims) return undefined
    if (aspectRatio != null) return { aspectRatio: String(aspectRatio) }
    if (!hasOwnAspect(className)) return { aspectRatio: '16 / 9' }
    return undefined
  })()

  return (
    <div className={`relative overflow-hidden ${className}`} style={wrapperStyle}>
      {!loaded && (
        <div className={`absolute inset-0 ${placeholderClassName}`} />
      )}
      {/* `contents` so <picture> generates no box: the <img>'s `h-full` keeps
          resolving against the wrapper div, exactly as it did before. */}
      <picture className="contents">
        {resolvedSources?.map((source) => (
          <source key={source.type} type={source.type} srcSet={source.srcSet} sizes={resolvedSizes} />
        ))}
        <img
          ref={measureRef}
          src={imgSrc}
          srcSet={resolvedSrcSet}
          sizes={resolvedSizes}
          alt={alt}
          width={hasDims ? width : undefined}
          height={hasDims ? height : undefined}
          loading={lazy && fetchPriority !== 'high' ? 'lazy' : 'eager'}
          decoding="async"
          fetchPriority={fetchPriority}
          onLoad={(event) => {
            setLoaded(true)
            // A swap to the working `fallback` has to lift the mark, or the
            // recovered photo stays invisible under the CSS rule.
            clearImageFailed(event.currentTarget)
          }}
          onError={(event) => {
            markImageFailed(event.currentTarget)
            if (!error) setError(true)
          }}
          className={`w-full h-full object-cover transition-opacity duration-300 ${loaded ? 'opacity-100' : 'opacity-0'}`}
        />
      </picture>
    </div>
  )
}
